import { useCurrentProject, WithFeatureEnabled } from 'src/providers/current-project.provider'
import { ProjectService } from 'src/services/project.service'
import { useTranslation } from 'react-i18next'
import { Statistic, StatisticsBloc, SubStatistic } from './statistic.component'
import {
  CurecallBanner,
  CurecallButton,
  CurecallDatePicker,
  CurecallModalContainer,
  CurecallSelect,
  CurecallTooltip,
  ECurecallButtonAppearance,
  FileTextIcon,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'
import { useEffect, useState } from 'react'
import PuzzlePieceImage from '/assets/img/puzzle-pieces.svg'
import { useNotification } from 'src/providers/notification.provider'

type TStatistic = {
  name: string
  label: string
  value: string
  subStats?: TStatistic[]
}

type TFormStatistics = {
  formName: string
  label: string
  completionRate: string
  patientVariablesStatistics: TStatistic[]
}

interface Props {
  projectStatistics: any
}

const FormStatistics = ({ projectStatistics }: Props) => {
  const { t } = useTranslation()
  const { project, availableMedEventOptions } = useCurrentProject()
  const { notify } = useNotification()

  const [showNoDataToDownloadBanner, setShowNoDataToDownloadBanner] = useState(false)
  const [selectedMedEventIds, setSelectedMedEventIds] = useState<number[]>([])
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [minDate, setMinDate] = useState<string | null>(null)
  const [maxDate, setMaxDate] = useState<string | null>(null)
  const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx')

  // Select all appointments by default when options are available
  useEffect(() => {
    if (selectedMedEventIds?.length === 0 && availableMedEventOptions?.length > 0) {
      setSelectedMedEventIds(availableMedEventOptions.map((opt) => opt.value))
    }
    // We intentionally do not add selectedMedEvents to deps to avoid overriding user selections
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMedEventOptions])

  const downloadPatientFormsCsv = async () => {
    try {
      const result = await ProjectService.getPatientFormsCsvBlob(project.id)
      if (result.status === 204) {
        setShowNoDataToDownloadBanner(true)
        return
      }

      const blob = new Blob([result.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.href = url
      const contentDisposition =
        result?.headers?.['content-disposition'] || result?.headers?.['Content-Disposition']
      const filename = contentDisposition ? contentDisposition.split('filename=')[1] : null
      a.download = filename || 'curecall_patient_forms_export.csv'
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 0)
    } catch (error: any) {
      notify({
        title: t('common.error.generic'),
        message: error.message,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const isInvalidDateRange = Boolean(minDate && maxDate && minDate > maxDate)

  const downloadPatientMedEventsXlsx = async () => {
    try {
      const medEventIds = selectedMedEventIds
      const result = await ProjectService.getPatientMedEventXlsxBlob(
        project.id,
        medEventIds,
        minDate || undefined,
        maxDate || undefined,
      )
      if (result.status === 204) {
        setShowNoDataToDownloadBanner(true)
        return
      }

      const blob = new Blob([result.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.href = url
      const contentDisposition =
        result?.headers?.['content-disposition'] || result?.headers?.['Content-Disposition']
      const filename = contentDisposition ? contentDisposition.split('filename=')[1] : null
      a.download = filename || 'curecall_patient_med_events.xlsx'
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 0)
    } catch (error: any) {
      notify({
        title: t('common.error.generic'),
        message: error.message,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const downloadPatientMedEventsCsv = async () => {
    try {
      const medEventIds = selectedMedEventIds
      const result = await ProjectService.getPatientMedEventCsvBlob(
        project.id,
        medEventIds,
        minDate || undefined,
        maxDate || undefined,
      )
      if (result.status === 204) {
        setShowNoDataToDownloadBanner(true)
        return
      }

      const blob = new Blob([result.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.href = url
      const contentDisposition =
        result?.headers?.['content-disposition'] || result?.headers?.['Content-Disposition']
      const filename = contentDisposition ? contentDisposition.split('filename=')[1] : null
      a.download = filename || 'curecall_patient_med_events.csv'
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 0)
    } catch (error: any) {
      notify({
        title: t('common.error.generic'),
        message: error.message,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const isStatisticPageUnderMaintenance = import.meta.env?.VITE_IS_STATISTIC_PAGE_ACTIVATED !== 'true'

  if (projectStatistics?.formsStatistics?.length === 0) {
    return null
  }

  return (
    <div className="c-mt-4">
      <div className="c-flex c-justify-between c-gap-4 c-items-start">
        <h2
          className={twMerge(
            'c-text-base c-font-nexa c-font-semibold',
            projectStatistics?.formsStatistics?.length > 1 && 'c-break-before-page c-break-after-avoid',
          )}
        >
          {t('pages.statistics.form-statistics-title')}
        </h2>
        <div className="c-ml-auto c-flex c-items-start c-gap-3 hide-for-print">
          <CurecallTooltip content={t('pages.statistics.download-csv-tooltip')} tooltipPosition="top">
            <CurecallButton
              appearance={ECurecallButtonAppearance.SECONDARY}
              onClick={downloadPatientFormsCsv}
            >
              <div className="c-flex c-items-center">
                <FileTextIcon className="c-w-4 c-mr-2" />
                {t('pages.statistics.download-csv')}
              </div>
            </CurecallButton>
          </CurecallTooltip>

          <WithFeatureEnabled featuresAccepted={['patient_med_events_export_tool']}>
            <div className="c-flex c-items-end c-gap-2">
              <CurecallButton
                appearance={ECurecallButtonAppearance.PRIMARY}
                onClick={() => setIsExportModalOpen(true)}
                isDisabled={availableMedEventOptions?.length === 0}
              >
                {t('pages.statistics.open-export-modal')}
              </CurecallButton>
            </div>
          </WithFeatureEnabled>
        </div>
      </div>

      {showNoDataToDownloadBanner && (
        <CurecallBanner className="c-mt-6" appearance="ERROR">
          {t('pages.statistics.no-data-to-download')}
        </CurecallBanner>
      )}

      {/* Export modal */}
      <CurecallModalContainer
        isOpen={isExportModalOpen}
        toggle={() => setIsExportModalOpen((v) => !v)}
        closeModalAriaLabel={t('common.actions.close')}
        dialogClassName="c-w-2/3"
      >
        <div className="c-pt-6 c-px-8 c-pb-4 c-border-b c-border-b-grey-200">
          <h3>{t('pages.statistics.export-modal.title')}</h3>
          <p className="c-text-grey-600 c-mt-1">{t('pages.statistics.export-modal.subtitle')}</p>
        </div>
        <div className="c-p-8 c-flex c-flex-col c-gap-6 c-flex-1">
          <div>
            <div className="c-flex c-items-center c-mb-2">
              <p className="c-text-sm c-font-semibold">{t('pages.statistics.med-events-select-label')}</p>
              <button
                type="button"
                className={twMerge(
                  'c-ml-auto c-text-primary c-font-bold hover:c-underline',
                  availableMedEventOptions?.length === 0 &&
                    'c-text-grey-400 c-cursor-not-allowed hover:c-no-underline',
                )}
                onClick={() => {
                  if (availableMedEventOptions?.length === 0) return
                  const areAllSelected =
                    selectedMedEventIds.length > 0 &&
                    selectedMedEventIds.length === availableMedEventOptions?.length
                  if (areAllSelected) {
                    setSelectedMedEventIds([])
                  } else {
                    setSelectedMedEventIds(availableMedEventOptions?.map((opt) => opt.value))
                  }
                }}
                aria-label={
                  selectedMedEventIds?.length === availableMedEventOptions?.length &&
                  availableMedEventOptions?.length > 0
                    ? t('pages.statistics.unselect-all')
                    : t('pages.statistics.select-all')
                }
                disabled={availableMedEventOptions?.length === 0}
              >
                {selectedMedEventIds?.length === availableMedEventOptions?.length &&
                availableMedEventOptions?.length > 0
                  ? t('pages.statistics.unselect-all')
                  : t('pages.statistics.select-all')}
              </button>
            </div>
            <div className="c-border c-border-grey-200 c-rounded-md c-p-3 tw-max-h-[420px] tw-overflow-y-auto tw-overflow-x-hidden">
              <div className="c-flex c-flex-wrap c-gap-2">
                {availableMedEventOptions?.map((opt) => {
                  const isSelected = selectedMedEventIds.includes(opt.value)
                  return (
                    <label
                      key={opt.value}
                      className={twMerge(
                        'c-inline-flex c-items-center c-gap-2 c-rounded-full c-border c-px-3 c-py-1 c-cursor-pointer c-select-none',
                        isSelected ? 'c-bg-primary-50 c-border-primary-300' : 'c-bg-white c-border-grey-300',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="c-peer c-sr-only"
                        checked={isSelected}
                        onChange={() => {
                          setSelectedMedEventIds((prev) =>
                            prev.includes(opt.value)
                              ? prev.filter((id) => id !== opt.value)
                              : [...prev, opt.value],
                          )
                        }}
                        aria-label={opt.label}
                      />
                      <span
                        className={twMerge(
                          'c-inline-flex c-items-center c-justify-center c-w-4 c-h-4 c-rounded c-border',
                          isSelected
                            ? 'c-border-primary-500 c-bg-primary-50'
                            : 'c-border-grey-400 c-bg-white',
                        )}
                        aria-hidden
                      >
                        {isSelected && (
                          <svg className="c-w-3 c-h-3 c-text-primary" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414l2.293 2.293 6.543-6.543a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                      <span className="c-text-sm">{opt.label}</span>
                    </label>
                  )
                })}
              </div>
            </div>
            <div className="c-text-xs c-text-grey-600 c-mt-2">
              {t('pages.statistics.med-events-selected', { number: selectedMedEventIds.length })}
            </div>
          </div>
          <div className="c-flex c-gap-4">
            <div className="c-w-[260px]">
              <CurecallDatePicker
                label={t('pages.statistics.export-modal.min-date')}
                onChangeDate={(date: string | null) => setMinDate(date)}
              />
            </div>
            <div className="c-w-[260px]">
              <CurecallDatePicker
                label={t('pages.statistics.export-modal.max-date')}
                onChangeDate={(date: string | null) => setMaxDate(date)}
              />
            </div>
            <CurecallSelect
              label={t('pages.statistics.export-format')}
              items={[
                { label: t('pages.statistics.format.xlsx'), value: 'xlsx' },
                { label: t('pages.statistics.format.csv'), value: 'csv' },
              ]}
              selectedItem={{
                label:
                  exportFormat === 'xlsx'
                    ? t('pages.statistics.format.xlsx')
                    : t('pages.statistics.format.csv'),
                value: exportFormat,
              }}
              onChange={(item: any) => setExportFormat(item?.value)}
            />
          </div>
          {isInvalidDateRange && (
            <CurecallBanner appearance="WARNING">
              {t('pages.statistics.export-modal.invalid-range')}
            </CurecallBanner>
          )}

          <div className="c-flex c-justify-end c-gap-3 c-mt-2 c-items-center">
            <CurecallButton
              appearance={ECurecallButtonAppearance.CANCEL}
              onClick={() => setIsExportModalOpen(false)}
            >
              {t('components.modals.cancel')}
            </CurecallButton>
            <div className="c-flex c-items-center c-gap-2">
              <CurecallButton
                appearance={ECurecallButtonAppearance.PRIMARY}
                onClick={async () => {
                  if (exportFormat === 'csv') {
                    await downloadPatientMedEventsCsv()
                  } else {
                    await downloadPatientMedEventsXlsx()
                  }
                  setIsExportModalOpen(false)
                }}
                isDisabled={selectedMedEventIds.length === 0 || isInvalidDateRange}
                className="c-min-w-[260px] c-text-nowrap c-justify-center"
              >
                {t('pages.statistics.download-med-events')}
              </CurecallButton>
            </div>
          </div>
        </div>
      </CurecallModalContainer>

      {isStatisticPageUnderMaintenance ? (
        <div className="c-flex c-items-center c-flex-col c-mx-52">
          <img src={PuzzlePieceImage} alt="" />
          <p className="c-mt-4 c-text-center">{t('pages.statistics.maintenance')}</p>
        </div>
      ) : (
        /* c-table is required to print pdf correctly with page break, see: https://stackoverflow.com/questions/20408033/how-to-get-page-break-inside-avoid-to-work-nicely-with-flex-wrap-wrap/42806935#42806935 */
        <div className="c-pb-10 c-w-full c-table">
          {projectStatistics.formsStatistics?.map((formStatistics: TFormStatistics) => {
            return (
              <StatisticsBloc
                key={formStatistics.formName}
                isSmallBloc={projectStatistics?.formsStatistics?.length > 1}
              >
                <Statistic
                  label={formStatistics.label}
                  value={formStatistics.completionRate}
                  isEmphased={true}
                />
                {formStatistics.patientVariablesStatistics.map((formStatistic: TStatistic) => {
                  return (
                    <div key={`${formStatistics.formName}-${formStatistic.name}`}>
                      <Statistic label={formStatistic.label} value={formStatistic.value} />
                      {formStatistic?.subStats?.map((subStat) => (
                        <SubStatistic
                          key={`${formStatistics.formName}-${formStatistic.name}-${subStat.label}`}
                          label={subStat.label}
                          value={subStat.value + '%'}
                        />
                      ))}
                    </div>
                  )
                })}
              </StatisticsBloc>
            )
          })}
        </div>
      )}
    </div>
  )
}

export { FormStatistics }
